import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PLATFORM_CATALOG } from "@/lib/constants";
import { BrandMark } from "@/components/Brand";
import {
  Loader2, Check, Save, Building2, Sparkles, ArrowRight, ArrowLeft,
  Rocket, Target, Compass, Wand2, PencilLine,
} from "lucide-react";

const EMPTY = {
  business_name: "",
  owner_name: "",
  business_description: "",
  target_audience: "",
  products_services: "",
  brand_voice: "",
  primary_platforms: [],
  business_goals: "",
  content_focus: "",
  ai_context: "",
};

const PLATFORM_HINT =
  "If you skip this, a sensible default set (Blog, Facebook, Instagram, LinkedIn, Pinterest, Email, YouTube) is used.";

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground -mt-1">{hint}</p>}
      {children}
    </div>
  );
}

function PlatformPicker({ selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="setup-platforms">
      {PLATFORM_CATALOG.map((p) => {
        const active = (selected || []).includes(p.key);
        return (
          <button
            type="button"
            key={p.key}
            onClick={() => onToggle(p.key)}
            data-testid={`setup-platform-${p.key}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${
              active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-foreground/70 border-border hover:bg-muted"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// Wizard step definitions (welcome + 5 field steps + review)
const STEPS = [
  { key: "welcome" },
  { key: "basics" },
  { key: "about" },
  { key: "offers" },
  { key: "voice" },
  { key: "focus" },
  { key: "review" },
];

const STEP_META = {
  basics: { icon: Building2, title: "Your business basics", sub: "Let us start with the essentials." },
  about: { icon: Compass, title: "What your business does", sub: "Describe your business and who you serve." },
  offers: { icon: Rocket, title: "Your offers", sub: "What you sell or give away." },
  voice: { icon: Sparkles, title: "Voice & platforms", sub: "How you sound and where you show up." },
  focus: { icon: Target, title: "Focus & goals", sub: "What you are working toward." },
  review: { icon: Check, title: "Review & finish", sub: "A quick look before you save." },
};

const REVIEW_ROWS = [
  ["Business name", "business_name"],
  ["Owner / preferred name", "owner_name"],
  ["What your business does", "business_description"],
  ["Target audience", "target_audience"],
  ["Products, services & offers", "products_services"],
  ["Brand voice", "brand_voice"],
  ["Content focus", "content_focus"],
  ["Business goals", "business_goals"],
  ["Context for the AI Guide", "ai_context"],
];

export default function BusinessSetup() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [mode, setMode] = useState("edit"); // "wizard" | "edit"
  const [step, setStep] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/business-setup");
      const data = { ...EMPTY, ...(r.data || {}) };
      setDoc(data);
      // Brand-new owners (setup never completed) get the guided wizard.
      setMode(data.completed ? "edit" : "wizard");
      setStep(0);
    } catch (e) {
      toast.error("Couldn't load your business setup. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => {
    setDoc((d) => ({ ...d, [k]: v }));
    setSaved(false);
  };

  const togglePlatform = (key) => {
    setDoc((d) => {
      const has = (d.primary_platforms || []).includes(key);
      const next = has
        ? d.primary_platforms.filter((k) => k !== key)
        : [...(d.primary_platforms || []), key];
      return { ...d, primary_platforms: next };
    });
    setSaved(false);
  };

  const save = async ({ complete = false, silent = false } = {}) => {
    setSaving(true);
    try {
      const payload = { ...doc, completed: complete ? true : doc.completed };
      const r = await api.put("/business-setup", payload);
      setDoc({ ...EMPTY, ...(r.data || {}) });
      setSaved(true);
      if (!silent) toast.success("Business setup saved. The OS and AI Business Guide now use your information.");
      setTimeout(() => setSaved(false), 2500);
      return true;
    } catch (e) {
      toast.error("Couldn't save your business setup. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finishWizard = async () => {
    const ok = await save({ complete: true });
    if (ok) {
      setMode("edit");
      toast.success("You are all set. Welcome to Work & Keep.");
      navigate("/content");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-primary" data-testid="setup-loading" />
      </div>
    );
  }

  // -------------------------- WIZARD --------------------------
  if (mode === "wizard") {
    const current = STEPS[step];
    const total = STEPS.length;
    const pct = Math.round((step / (total - 1)) * 100);
    const canProceed = current.key !== "basics" || (doc.business_name || "").trim().length > 0;

    const goNext = () => setStep((s) => Math.min(s + 1, total - 1));
    const goBack = () => setStep((s) => Math.max(s - 1, 0));

    return (
      <div className="max-w-2xl mx-auto" data-testid="business-setup-page">
        <div data-testid="setup-wizard">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground" data-testid="wizard-step-indicator">
                Step {step + 1} of {total}
              </span>
              <button
                type="button"
                onClick={() => navigate("/content")}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                data-testid="wizard-skip"
              >
                Skip for now
              </button>
            </div>
            <Progress value={pct} className="h-1.5" />
          </div>

          <Card className="rounded-3xl p-7 sm:p-9 border-border shadow-soft">
            {current.key === "welcome" && (
              <div className="text-center py-4" data-testid="wizard-welcome">
                <div className="mx-auto mb-5 flex items-center justify-center">
                  <BrandMark className="h-16 w-16 object-contain" />
                </div>
                <h1 className="font-heading text-3xl mb-3">Welcome to Work & Keep</h1>
                <p className="text-muted-foreground leading-relaxed max-w-md mx-auto">
                  {"Let's set up your business in a few quick steps. This is what powers your whole command center, including the AI Business Guide, so it works in your voice, for your audience. It takes about two minutes, and you can change anything later."}
                </p>
              </div>
            )}

            {current.key !== "welcome" && current.key !== "review" && (
              <StepHeader meta={STEP_META[current.key]} />
            )}

            {current.key === "basics" && (
              <div className="space-y-5">
                <Field label="Business name" hint="What do you call your business?">
                  <Input value={doc.business_name} onChange={(e) => set("business_name", e.target.value)}
                    placeholder="e.g. Northwind Studio" className="rounded-xl" data-testid="setup-business-name" autoFocus />
                  {!canProceed && <p className="text-xs text-destructive mt-1">Add a business name to continue.</p>}
                </Field>
                <Field label="Owner / preferred name" hint="What should the AI call you?">
                  <Input value={doc.owner_name} onChange={(e) => set("owner_name", e.target.value)}
                    placeholder="e.g. Alex" className="rounded-xl" data-testid="setup-owner-name" />
                </Field>
              </div>
            )}

            {current.key === "about" && (
              <div className="space-y-5">
                <Field label="What your business does" hint="A few sentences on what you offer and the value you provide.">
                  <Textarea value={doc.business_description} onChange={(e) => set("business_description", e.target.value)}
                    placeholder="Describe your business, what you offer, and the transformation or value you provide."
                    className="rounded-xl min-h-[110px]" data-testid="setup-description" autoFocus />
                </Field>
                <Field label="Target audience" hint="Who you serve, and what they most want or struggle with.">
                  <Textarea value={doc.target_audience} onChange={(e) => set("target_audience", e.target.value)}
                    placeholder="e.g. Early-stage founders who need help with marketing but have no time."
                    className="rounded-xl min-h-[90px]" data-testid="setup-audience" />
                </Field>
              </div>
            )}

            {current.key === "offers" && (
              <div className="space-y-5">
                <Field label="Products, services & offers" hint="List what you sell or offer, one per line.">
                  <Textarea value={doc.products_services} onChange={(e) => set("products_services", e.target.value)}
                    placeholder={"e.g.\nStarter Kit, $29, active\n1:1 Coaching, $500/mo, active\nFree checklist, lead magnet"}
                    className="rounded-xl min-h-[130px]" data-testid="setup-products" autoFocus />
                </Field>
              </div>
            )}

            {current.key === "voice" && (
              <div className="space-y-5">
                <Field label="Brand voice" hint="How your writing should feel and sound.">
                  <Textarea value={doc.brand_voice} onChange={(e) => set("brand_voice", e.target.value)}
                    placeholder="e.g. Clear, confident and practical. Friendly but never fluffy."
                    className="rounded-xl min-h-[90px]" data-testid="setup-voice" autoFocus />
                </Field>
                <Field label="Primary platforms" hint="Choose where you publish, your weekly content kit is built from these.">
                  <PlatformPicker selected={doc.primary_platforms} onToggle={togglePlatform} />
                  {(doc.primary_platforms || []).length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{PLATFORM_HINT}</p>
                  )}
                </Field>
              </div>
            )}

            {current.key === "focus" && (
              <div className="space-y-5">
                <Field label="Content focus" hint="The core topics, themes or pillars your content centers on.">
                  <Textarea value={doc.content_focus} onChange={(e) => set("content_focus", e.target.value)}
                    placeholder="e.g. Practical marketing, productivity systems, founder mindset."
                    className="rounded-xl min-h-[80px]" data-testid="setup-content-focus" autoFocus />
                </Field>
                <Field label="Business goals" hint="What you are working toward right now.">
                  <Textarea value={doc.business_goals} onChange={(e) => set("business_goals", e.target.value)}
                    placeholder="e.g. Grow the email list to 5,000 and launch a paid course this quarter."
                    className="rounded-xl min-h-[80px]" data-testid="setup-goals" />
                </Field>
                <Field label="Important context for the AI Guide" hint="Anything else the AI should always keep in mind.">
                  <Textarea value={doc.ai_context} onChange={(e) => set("ai_context", e.target.value)}
                    placeholder="e.g. Avoid jargon. Always suggest one concrete next step."
                    className="rounded-xl min-h-[90px]" data-testid="setup-ai-context" />
                </Field>
              </div>
            )}

            {current.key === "review" && (
              <div data-testid="wizard-review">
                <StepHeader meta={STEP_META.review} />
                <div className="space-y-3">
                  {REVIEW_ROWS.map(([label, key]) => (
                    <div key={key} className="flex flex-col sm:flex-row sm:gap-4 py-2 border-b border-border/60 last:border-0">
                      <span className="text-xs font-medium text-muted-foreground sm:w-56 shrink-0">{label}</span>
                      <span className="text-sm whitespace-pre-line">{(doc[key] || "").trim() || <span className="text-muted-foreground/60">not set</span>}</span>
                    </div>
                  ))}
                  <div className="flex flex-col sm:flex-row sm:gap-4 py-2">
                    <span className="text-xs font-medium text-muted-foreground sm:w-56 shrink-0">Primary platforms</span>
                    <span className="text-sm">
                      {(doc.primary_platforms || []).length
                        ? (doc.primary_platforms || [])
                            .map((k) => (PLATFORM_CATALOG.find((p) => p.key === k) || {}).label || k)
                            .join(", ")
                        : <span className="text-muted-foreground/60">Default set (Blog, Facebook, Instagram, LinkedIn, Pinterest, Email, YouTube)</span>}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" onClick={goBack} disabled={step === 0 || saving}
                className="rounded-full gap-2" data-testid="wizard-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              {current.key === "review" ? (
                <Button onClick={finishWizard} disabled={saving} className="rounded-full px-6 gap-2" data-testid="wizard-finish">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {saving ? "Saving…" : "Finish setup"}
                </Button>
              ) : (
                <Button onClick={goNext} disabled={!canProceed || saving} className="rounded-full px-6 gap-2" data-testid="wizard-next">
                  {step === 0 ? "Let's go" : "Next"} <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // -------------------------- EDIT VIEW --------------------------
  return (
    <div data-testid="business-setup-page">
      <div data-testid="setup-edit-view" />
      <PageHeader
        title="Business Setup"
        subtitle="Everything, including the AI Business Guide, uses this to work in your voice, for your audience."
        testid="business-setup-header"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setMode("wizard"); setStep(0); }}
              className="rounded-full gap-2 border-border" data-testid="run-wizard-btn">
              <Wand2 className="h-4 w-4" /> Guided setup
            </Button>
            <Button onClick={() => save()} disabled={saving} data-testid="save-setup-btn" className="rounded-full px-6 gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : saved ? "Saved" : "Save setup"}
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        <SectionCard icon={Building2} title="Your business" description="The essentials that describe who you are and what you do.">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Business name">
              <Input value={doc.business_name} onChange={(e) => set("business_name", e.target.value)}
                placeholder="e.g. Northwind Studio" className="rounded-xl" data-testid="setup-business-name" />
            </Field>
            <Field label="Owner / preferred name">
              <Input value={doc.owner_name} onChange={(e) => set("owner_name", e.target.value)}
                placeholder="e.g. Alex" className="rounded-xl" data-testid="setup-owner-name" />
            </Field>
          </div>
          <Field label="What your business does" hint="A clear description of your business in a few sentences.">
            <Textarea value={doc.business_description} onChange={(e) => set("business_description", e.target.value)}
              placeholder="Describe your business, what you offer, and the transformation or value you provide."
              className="rounded-xl min-h-[100px]" data-testid="setup-description" />
          </Field>
          <Field label="Target audience" hint="Who you serve, and what they most want or struggle with.">
            <Textarea value={doc.target_audience} onChange={(e) => set("target_audience", e.target.value)}
              placeholder="e.g. Early-stage founders who need help with marketing but have no time."
              className="rounded-xl min-h-[90px]" data-testid="setup-audience" />
          </Field>
          <Field label="Products, services & offers" hint="List what you sell or offer, one per line.">
            <Textarea value={doc.products_services} onChange={(e) => set("products_services", e.target.value)}
              placeholder={"e.g.\nStarter Kit, $29, active\n1:1 Coaching, $500/mo, active\nFree checklist, lead magnet"}
              className="rounded-xl min-h-[100px]" data-testid="setup-products" />
          </Field>
        </SectionCard>

        <SectionCard icon={Sparkles} title="Voice & content" description="How you sound and where you show up. This shapes everything the AI creates.">
          <Field label="Brand voice" hint="How your writing should feel and sound.">
            <Textarea value={doc.brand_voice} onChange={(e) => set("brand_voice", e.target.value)}
              placeholder="e.g. Clear, confident and practical. Friendly but never fluffy."
              className="rounded-xl min-h-[80px]" data-testid="setup-voice" />
          </Field>
          <Field label="Primary platforms" hint="Choose the platforms you publish on. Your weekly content kit is built from these.">
            <PlatformPicker selected={doc.primary_platforms} onToggle={togglePlatform} />
            {(doc.primary_platforms || []).length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">{PLATFORM_HINT}</p>
            )}
          </Field>
          <Field label="Content focus" hint="The core topics, themes or pillars your content centers on.">
            <Textarea value={doc.content_focus} onChange={(e) => set("content_focus", e.target.value)}
              placeholder="e.g. Practical marketing, productivity systems, founder mindset."
              className="rounded-xl min-h-[80px]" data-testid="setup-content-focus" />
          </Field>
        </SectionCard>

        <SectionCard icon={Target} title="Goals & AI context" description="Give the AI Business Guide the bigger picture so its advice fits your business.">
          <Field label="Business goals" hint="What you are working toward right now.">
            <Textarea value={doc.business_goals} onChange={(e) => set("business_goals", e.target.value)}
              placeholder="e.g. Grow the email list to 5,000 and launch a paid course this quarter."
              className="rounded-xl min-h-[80px]" data-testid="setup-goals" />
          </Field>
          <Field label="Important context for the AI Guide" hint="Anything else the AI should always keep in mind.">
            <Textarea value={doc.ai_context} onChange={(e) => set("ai_context", e.target.value)}
              placeholder="e.g. Avoid jargon. Always suggest one concrete next step. My competitors are X and Y."
              className="rounded-xl min-h-[100px]" data-testid="setup-ai-context" />
          </Field>
        </SectionCard>
      </div>
    </div>
  );
}

function StepHeader({ meta }) {
  if (!meta) return null;
  const Icon = meta.icon || PencilLine;
  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
      </div>
      <div>
        <h2 className="font-heading text-2xl leading-tight">{meta.title}</h2>
        {meta.sub && <p className="text-sm text-muted-foreground mt-1">{meta.sub}</p>}
      </div>
    </div>
  );
}

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <Card className="rounded-3xl p-6 sm:p-7 border-border shadow-soft">
      <div className="flex items-start gap-3 mb-5">
        {Icon && (
          <div className="h-9 w-9 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} />
          </div>
        )}
        <div>
          <h2 className="font-heading text-xl leading-tight">{title}</h2>
          {description && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{description}</p>}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </Card>
  );
}
